import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { STUDIO_THEMES, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../../common/types';
import { studioThemeMessages } from '../studioThemeMessages';
import { NextUp, NextUpMoment } from './nextUpAssetTypes';
import { songPickerMessages as m } from './songPickerMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './SongPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

const TITLE: Readonly<Record<NextUpMoment, MessageDescriptor>> = {
  noMaster: m.nextNoMaster,
  beforeRelease: m.nextBeforeRelease,
  outNow: m.nextOutNow,
  catalog: m.nextCatalog,
};

/** The name of a chip: the story and the feed post say their moment; the other types use their name. */
function chipName(moment: NextUpMoment, assetTypeId: StudioThemeId): MessageDescriptor {
  if (assetTypeId === 'story') {
    return moment === 'beforeRelease' ? m.chipPreSaveStory : m.chipOutNowStory;
  }
  if (assetTypeId === 'feedPost') {
    return m.chipAnnouncePost;
  }
  return studioThemeMessages[assetTypeId].name;
}

interface SongPickerNextUpProps {
  nextUp: NextUp;
  onPick: (assetTypeId: StudioThemeId) => void;
}

/** "Next up" (design t1): two asset types for the moment of the song, with their cost. */
const SongPickerNextUpComponent: React.FC<SongPickerNextUpProps> = ({ nextUp, onPick }) => {
  const intl = useIntl();
  return (
    <div className={styles.nextUp}>
      <span className={styles.eyebrow}>{intl.formatMessage(TITLE[nextUp.moment])}</span>
      <div className={styles.nextRow}>
        {nextUp.assetTypeIds.map((assetTypeId) => {
          const { credits } = STUDIO_THEMES[assetTypeId];
          return (
            <button
              key={assetTypeId}
              type="button"
              className={styles.nextChip}
              data-testid={`song-picker-next-${assetTypeId}`}
              onClick={() => onPick(assetTypeId)}
            >
              {intl.formatMessage(chipName(nextUp.moment, assetTypeId))}
              <small>
                {credits === 0 ? intl.formatMessage(m.costFree) : intl.formatMessage(m.costCredits, { credits })}
              </small>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const SongPickerNextUp = TypedMemo(SongPickerNextUpComponent);
